/**
 * Reads a short message to screen reader users without moving focus, through one polite live region
 * shared by the whole page.
 *
 * The region is created on first use and then stays in the document. A live region that is inserted
 * together with its text is often not read, so the message is added a moment after the region
 * exists. Each message is its own node, so two messages in a row are both read, and each is removed
 * after a while, so the virtual cursor doesn't find old messages at the end of the page.
 */

const REGION_ID = 'joykit-live-announcer';
/** Long enough for the region to be registered before its first message arrives. */
const ANNOUNCE_DELAY_MS = 100;
/** Long enough for a screen reader to finish a queued message. */
const MESSAGE_LIFETIME_MS = 7000;

const visuallyHidden: Partial<CSSStyleDeclaration> = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  margin: '-1px',
  padding: '0',
  border: '0',
  overflow: 'hidden',
  clip: 'rect(0px, 0px, 0px, 0px)',
  whiteSpace: 'nowrap',
};

const getRegion = (): HTMLElement => {
  const existing = document.getElementById(REGION_ID);
  if (existing) return existing;
  const region = document.createElement('div');
  region.id = REGION_ID;
  region.setAttribute('aria-live', 'polite');
  region.setAttribute('aria-relevant', 'additions');
  Object.assign(region.style, visuallyHidden);
  document.body.appendChild(region);
  return region;
};

/** Reads the message out. Returns a function that withdraws it, if it hasn't been read yet. */
export const announcePolitely = (message: string): (() => void) => {
  const text = message.trim();
  if (!text || typeof document === 'undefined') return () => undefined;
  const region = getRegion();
  const timer = window.setTimeout(() => {
    const node = document.createElement('div');
    node.textContent = text;
    region.appendChild(node);
    window.setTimeout(() => node.remove(), MESSAGE_LIFETIME_MS);
  }, ANNOUNCE_DELAY_MS);
  return () => window.clearTimeout(timer);
};
